import { ICommandBarItemProps } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPCommandBar, IPSearchBox } from 'baseComponents';
import { IPContextualMenuItemType } from 'baseComponents/ContextualMenu/IPContextualMenu';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPSelection } from 'baseComponents/Utils/Selection';
import TemplateCriteriaUpgradeWizard from 'components/AuditCriteria/TemplateCriteriaUpgradeWizard';
import SingleTask from 'components/Tasks/SingleTask';
import { globalFilterDelay } from 'globalConstants';
import {
  approvalIcon,
  eventIcon,
  globalSearchBoxStyles,
  globalStackStylesHeight100PaddingScene,
  globalStackTokensGapMedium,
  taskIcon,
  templateIcon,
} from 'globalStyles';
import { PermissionTypes } from 'models/auth/rolePermission';
import { isAuditCriteriaPreviewEnabled } from 'models/copilot/copilotPreviewFeatureToggles';
import Task, { TaskTypes } from 'models/tasks/task';
import TaskTemplateScene from 'models/tasks/taskTemplateScene';
import { SystemTaskTypes, TaskKinds } from 'models/tasks/taskType';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { apiGetTaskTemplates } from 'services/Api/taskService';
import { apiRequest } from 'services/Auth/authConfig';
import {
  FeatureTypes,
  hasUserFeature,
  hasUserFeatureGenericManager,
  hasUserRolePermission,
} from 'services/Auth/featurePermissions';
import { TaskTemplateDetailsList } from './TaskTemplateDetailsList';

interface ITabTemplates {}

export const TabTemplates = (props: ITabTemplates) => {
  const { t } = useTranslation(['translation', 'tasks', 'task']);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [templateData, setTemplateData] = React.useState<TaskTemplateScene>(new TaskTemplateScene());
  const [editTask, setEditTask] = React.useState<Task | undefined>(undefined);
  const [newTask, setNewTask] = React.useState<Task | undefined>(undefined);
  const [searchTimer, setSearchTimer] = React.useState<NodeJS.Timeout | undefined>(undefined);
  const [searchQuery, setSearchQuery] = React.useState<string>();
  const appContext = React.useContext(AppContext);
  const location = useLocation();
  // one selected template, for "Upgrade to audit criteria" (PLAN-CTI-085)
  const [selectedTemplate, setSelectedTemplate] = React.useState<Task | undefined>(undefined);
  const [upgradeTemplate, setUpgradeTemplate] = React.useState<Task | undefined>(undefined);
  const [selection] = React.useState<IPSelection>(() => {
    const created: IPSelection = new IPSelection({
      getKey: (item) => String((item as Task).taskId),
      onSelectionChanged: () => setSelectedTemplate(created.getSelection()[0] as Task | undefined),
    });

    return created;
  });

  //
  // Effects
  //
  React.useEffect(() => {
    loadData();

    return () => {
      if (searchTimer) {
        clearTimeout(searchTimer);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash]);

  const onChangeText = (event?: React.ChangeEvent<HTMLInputElement>, filterText?: string): void => {
    if (filterText) {
      setSearchQuery(filterText.toLowerCase());
    } else {
      setSearchQuery('');
    }
  };

  const loadData = async () => {
    try {
      setIsLoading(true);
      selection.setAllSelected(false);
      setSelectedTemplate(undefined);

      //force cache refresh because we call this load data after changing templates (add/update)
      //this is needed because the back-end does not return subtasks on add/update and the template cache needs subtasks
      await appContext.globalDataCache.tags.getItems();
      await appContext.globalDataCache.templates.getItems(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const taskTemplateSceneData = await apiGetTaskTemplates(accessToken, appContext.globalDataCache);

      //hide templates that are marked as hidden (processing activity approval templates)
      taskTemplateSceneData.tasks = taskTemplateSceneData.tasks.filter((t) => !t.hidden);

      if (!hasUserFeature(appContext, FeatureTypes.Events)) {
        taskTemplateSceneData.tasks = taskTemplateSceneData.tasks.filter((t) => t.taskType !== TaskTypes.EventTemplate);
      }

      taskTemplateSceneData.tasks.forEach((t) => (t.taskStates = appContext.globalDataCache.taskStates.items));
      taskTemplateSceneData.tasks.sort(
        (a, b) => (b.completed === undefined ? 1 : 0) - (a.completed === undefined ? 1 : 0),
      );

      setTemplateData(taskTemplateSceneData);

      const hashParams = new URLSearchParams(window.location.hash);
      const taskId = Number(hashParams.get('#taskId'));
      if (taskId) {
        setEditTask(taskTemplateSceneData.tasks.find((task) => task.taskId === taskId));
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  //
  // Template actions
  //
  const onRemove = async (item: Task) => {
    try {
      setIsLoading(true);
      const newData = templateData;
      newData.tasks = templateData.tasks.filter((_item_) => {
        return _item_.taskId !== item.taskId;
      });
      appContext.globalDataCache.templates.remove(item);
      setTemplateData(newData);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const onUpdate = (task: Task) => {
    //no update needed
  };

  const onSave = (task: Task, isNew: boolean, isCancelled: boolean) => {
    if (!isCancelled) {
      loadData();
    }
  };

  const updateTasks = (tasks: Task[]) => {
    const newData = templateData;
    newData.tasks = tasks;
    setTemplateData(newData);
  };

  const addTaskTemplate = () => {
    const newTask = new Task();
    newTask.taskType = TaskTypes.Template;
    newTask.taskMasterId = 0;
    newTask.taskStates = appContext.globalDataCache.taskStates.items;
    newTask.taskStateId = newTask.taskStates[0].taskStateId;
    newTask.name = t('translation:Task.Name.Default');
    newTask.creatorId = appContext.user.id;
    newTask.creator = appContext.user;
    newTask.ownerId = appContext.user.id;
    newTask.owner = appContext.user;

    setNewTask(newTask);
  };

  const addEventTemplate = () => {
    const newTask = new Task();
    newTask.taskType = TaskTypes.EventTemplate;
    newTask.taskMasterId = 0;
    newTask.taskStates = appContext.globalDataCache.taskStates.items;
    newTask.taskStateId = newTask.taskStates[0].taskStateId;
    newTask.name = t('translation:Task.Name.Default');
    newTask.creatorId = appContext.user.id;
    newTask.creator = appContext.user;
    newTask.ownerId = appContext.user.id;
    newTask.owner = appContext.user;

    setNewTask(newTask);
  };

  const addApprovalTemplate = () => {
    const newTask = new Task();
    newTask.taskType = TaskTypes.EventTemplate;
    newTask.taskMasterId = 0;
    newTask.taskTypeId = appContext.globalDataCache.taskTypes.getTypeOfSystemType(SystemTaskTypes.Approval)?.taskTypeId;
    newTask.setSystemTaskType(SystemTaskTypes.Approval);
    //a new approval template exists only in the browser, so it gets the approval kind; the server stamps it on save
    newTask.taskKind = TaskKinds.Approval;
    newTask.taskStates = appContext.globalDataCache.taskStates.items;
    newTask.taskStateId = newTask.getFirstState();
    newTask.name = t('translation:Task.Name.Default');
    newTask.creatorId = appContext.user.id;
    newTask.creator = appContext.user;
    newTask.ownerId = appContext.user.id;
    newTask.owner = appContext.user;

    setNewTask(newTask);
  };

  const getCommandBarItems = (): ICommandBarItemProps[] => {
    //an approval template needs the approval system form; without it the template would be saved with no form
    const approvalForm = appContext.globalDataCache.taskTypes.getTypeOfSystemType(SystemTaskTypes.Approval);

    const items: ICommandBarItemProps[] = [
      {
        className: 'isoplanner-tasks-template-add',
        key: 'add',
        disabled:
          !hasUserFeatureGenericManager(appContext) ||
          !hasUserRolePermission(appContext, PermissionTypes.CreateTask) ||
          isLoading,
        text: t('tasks:TabTemplates.Commands.Add'),
        iconProps: { iconName: 'Add' },
        subMenuProps: {
          items: [
            {
              key: 'header1',
              text: t('tasks:TabTemplates.Commands.HeaderGeneric'),
              itemType: IPContextualMenuItemType.Header,
            },
            {
              className: 'isoplanner-tasks-template-add-task',
              key: 'addTaskTemplate',
              text: t('tasks:TabTemplates.Commands.AddTaskTemplate'),
              iconProps: taskIcon,
              onClick: () => addTaskTemplate(),
            },
          ],
        },
      },
    ];

    if (hasUserFeature(appContext, FeatureTypes.Events)) {
      items[0].subMenuProps?.items.push(
        ...[
          {
            className: 'isoplanner-tasks-template-add-event',
            key: 'addEventTemplate',
            text: t('tasks:TabTemplates.Commands.AddEventTemplate'),
            iconProps: eventIcon,
            onClick: () => addEventTemplate(),
          },
        ],
      );

      if (hasUserFeature(appContext, FeatureTypes.Approvals)) {
        items[0].subMenuProps?.items.push(
          ...[
            {
              key: 'header2',
              text: t('tasks:TabTemplates.Commands.HeaderSpecific'),
              itemType: IPContextualMenuItemType.Header,
            },
            {
              className: 'isoplanner-tasks-template-add-approval',
              key: 'addApprovalTemplate',
              text: t('tasks:TabTemplates.Commands.AddApprovalTemplate'),
              title: approvalForm ? undefined : t('tasks:TabTemplates.Commands.AddApprovalTemplateNoForm'),
              disabled: !approvalForm,
              iconProps: approvalIcon,
              onClick: () => addApprovalTemplate(),
            },
          ],
        );
      }
    }

    // the selected template's checklist and form as criteria of a generic set, behind the generic criteria's own gate
    if (isAuditCriteriaPreviewEnabled(appContext) && hasUserFeatureGenericManager(appContext)) {
      const upgradable =
        selectedTemplate !== undefined &&
        (selectedTemplate.checkList.items.length > 0 || !!selectedTemplate.taskTypeId);
      items.push({
        key: 'upgradeToCriteria',
        text: t('tasks:TabTemplates.Commands.UpgradeToCriteria'),
        iconProps: templateIcon,
        disabled: !upgradable || isLoading,
        title: upgradable ? undefined : t('tasks:TabTemplates.Commands.UpgradeToCriteriaInfo'),
        onClick: () => setUpgradeTemplate(selectedTemplate),
      });
    }

    return items;
  };

  //
  // Main render
  //
  return (
    <>
      <IPStack verticalFill styles={globalStackStylesHeight100PaddingScene} tokens={globalStackTokensGapMedium}>
        <IPCommandBar styles={{ root: { paddingLeft: 0 } }} items={getCommandBarItems()}></IPCommandBar>
        <IPStack.Item>
          <IPSearchBox
            autoFocus={!appContext.isMobileView}
            underlined
            placeholder={t('translation:General.Filter.Placeholder')}
            id="search"
            onChange={(ev, newValue) => {
              if (searchTimer) {
                clearTimeout(searchTimer);
              }
              setSearchTimer(setTimeout(() => onChangeText(ev, newValue), globalFilterDelay));
            }}
            styles={globalSearchBoxStyles}
          />
        </IPStack.Item>
        <IPStack.Item verticalFill>
          <TaskTemplateDetailsList
            isBusy={isLoading}
            onEdit={(item: Task) => {
              setEditTask(item);
            }}
            data={templateData}
            searchQuery={searchQuery}
            updateTasks={updateTasks}
            selection={selection}
          />
        </IPStack.Item>
        {newTask && (
          <IPStack.Item>
            <SingleTask
              isOpen={newTask !== undefined}
              close={() => {
                setNewTask(undefined);
              }}
              task={newTask}
              onRemove={(item: Task) => {
                onRemove(item);
              }}
              onSave={onSave}
              onUpdate={onUpdate}
            />
          </IPStack.Item>
        )}
        {editTask && (
          <IPStack.Item>
            <SingleTask
              isOpen={editTask !== undefined}
              close={() => {
                setEditTask(undefined);
              }}
              task={editTask}
              onRemove={(item: Task) => {
                onRemove(item);
              }}
              onSave={onSave}
              onUpdate={onUpdate}
            />
          </IPStack.Item>
        )}
      </IPStack>
      {upgradeTemplate && (
        <TemplateCriteriaUpgradeWizard
          isOpen={true}
          template={upgradeTemplate}
          onClose={() => setUpgradeTemplate(undefined)}
        />
      )}
    </>
  );
};
